/**
 * MovingTypeSpecificationResponse → FlowDocument 어댑터.
 *
 * 이사 유형 spec(예: 일반이사·반포장이사·포장이사·대기이사·보관이사)을 item-picker
 * 의 sub-flow 로 변환한다. attribute 한 줄당 하나의 InquiryFlow question(또는 두 개의
 * sub-question — DATE_RANGE 한정)으로 매핑된다.
 *
 * 매핑 규칙(v2 — 정규화 인풋):
 *   STRING                          → text
 *   BOOLEAN                         → binary
 *   NUMBER / INT                    → suffix(number)
 *   DATE                            → date
 *   ENUM                            → single-select
 *   CHOICE_WITH_CUSTOM              → single-select + extensible(BottomSheet 직접 입력)
 *   TIME                            → time(BottomSheet HH:mm)
 *   STRUCTURED_DEPARTURE_ADDRESS    → address(BottomSheet 도로명 검색)
 *   DATE_RANGE                      → 두 개의 date question(`__start`, `__end`)
 *
 * 결과 답변은 `instancesToMovingTypeIntake` 가 backend `attributesByType[code]` 의
 * 평면 string map 으로 직렬화한다(객체/range 는 JSON 또는 sub-key 합성).
 *
 * 모든 question 은 짐 기록 sub-flow 와 동일하게 L2 layout(누적 cascade) 을 사용한다.
 * 마지막 question 의 ctaLabel 은 "완료", 그 외는 "다음".
 *
 * 결과 FlowDocument 는 `placeholder-auto` terminal 로 종료되어 InquiryFlow 가
 * `instance-done` 시점에 자동으로 onComplete 를 호출, ItemPicker 에 인스턴스를
 * 추가한다 (구조는 `specToFlowDocument` 와 동일).
 */

import type { FlowDocument } from '@miso/design-system';
import type {
  MovingTypeSpecificationAttribute,
  MovingTypeSpecificationResponse,
} from './movingTypeSpec';
import {
  resolveMovingTypeAttribute,
  type ResolvedMovingTypeAttributeType,
} from './resolveMovingTypeAttributeKind';
import { humanizeAttrLabel, humanizeEnumValue } from './movingTypeLabels';

type QuestionsStep = Extract<FlowDocument['steps'][string], { kind: 'questions' }>;
type QuestionDoc = QuestionsStep['questions'][number];

const SUB_FLOW_STEP_ID = 'fill';
const TERMINAL_DONE = 'instance-done';
const SUB_FLOW_LAYOUT = 'L2' as const;
const NEXT_LABEL = '다음';
const COMPLETE_LABEL = '완료';

/** Question id 가 InquiryFlow accumulated 에 저장되는 key 와 동일하다. */
export function attrToQuestionId(key: string): string {
  return `attr__${key}`;
}

/**
 * DATE_RANGE 는 시작·종료 두 sub-question 으로 분해된다.
 * `instancesToMovingTypeIntake` 가 두 sub-key 를 모아 `attr.${key}` = JSON 으로 직렬화.
 */
export const DATE_RANGE_START_SUFFIX = '__start';
export const DATE_RANGE_END_SUFFIX = '__end';

/** CHOICE_WITH_CUSTOM 의 직접 입력 트리거 sentinel. spec 에서 명시되지 않으면 사용. */
export const CHOICE_CUSTOM_TRIGGER = 'CUSTOM';

export interface MovingTypeSpecToFlowOptions {
  /** YYYY-MM-DD — 양일이사 boolean 질문 description 계산용 (장거리·제주/도서) */
  movingDate?: string | null;
}

/** API 가 아직 DATE_RANGE(overnightDateRange) 로 내려줄 때 boolean sub-flow 로 치환 */
const JEJU_OVERNIGHT_BOOLEAN_ATTRS: MovingTypeSpecificationAttribute[] = [
  {
    key: 'overnightPrevDayOk',
    name: '전일기준 양일이사 가능여부',
    type: 'BOOLEAN',
    optional: false,
    fieldKind: 'SCHEMA',
    helpText: null,
    enumValues: null,
  },
  {
    key: 'overnightNextDayOk',
    name: '다음날 기준 양일이사 가능여부',
    type: 'BOOLEAN',
    optional: false,
    fieldKind: 'SCHEMA',
    helpText: null,
    enumValues: null,
  },
];

/** sub-flow 빌드·저장 시 동일 attribute 집합을 쓰도록 정규화 */
export function resolveMovingTypeFlowAttributes(
  spec: MovingTypeSpecificationResponse
): MovingTypeSpecificationAttribute[] {
  const attrs = spec.attributes ?? [];
  if (
    spec.code === 'JEJU' &&
    attrs.some((a) => a.key === 'overnightDateRange') &&
    !attrs.some((a) => a.key === 'overnightPrevDayOk')
  ) {
    return JEJU_OVERNIGHT_BOOLEAN_ATTRS;
  }
  return attrs;
}

function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMonthDay(date: Date): string {
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** 전일·익일 양일이사 가능 여부 질문의 날짜 범위 description (예: "6월 3일 - 6월 4일") */
export function formatOvernightRangeDescription(
  movingDate: string | null | undefined,
  variant: 'prev' | 'next'
): string | undefined {
  if (!movingDate?.trim()) return undefined;
  const base = parseIsoDate(movingDate);
  if (!base) return undefined;
  if (variant === 'prev') {
    return `${formatMonthDay(addDays(base, -1))} - ${formatMonthDay(base)}`;
  }
  return `${formatMonthDay(base)} - ${formatMonthDay(addDays(base, 1))}`;
}

function overnightRangeDescription(
  attrKey: string,
  movingDate: string | null | undefined
): string | undefined {
  if (attrKey === 'overnightPrevDayOk') return formatOvernightRangeDescription(movingDate, 'prev');
  if (attrKey === 'overnightNextDayOk') return formatOvernightRangeDescription(movingDate, 'next');
  return undefined;
}

function attrToQuestions(
  attr: MovingTypeSpecificationAttribute,
  resolved: ResolvedMovingTypeAttributeType,
  options?: MovingTypeSpecToFlowOptions
): QuestionDoc[] {
  const id = attrToQuestionId(attr.key);
  const title = humanizeAttrLabel(attr.key, attr.name?.trim() ?? '', resolved.kind);
  const description = overnightRangeDescription(attr.key, options?.movingDate);
  const required = !attr.optional;
  // attr.helpText 는 backend 가 디버그성 영문(예: "StructuredDepartureAddress JSON")
  // 으로 채우는 경우가 있어 사용자 노출 정책에 부적합. description 은 사용자가
  // 명시 요청한 워딩만 유지한다는 정책에 따라 매핑하지 않는다.

  switch (resolved.kind) {
    case 'BOOLEAN':
      return [
        {
          id,
          title,
          ...(description ? { description } : {}),
          input: { kind: 'binary' as const, required },
        },
      ];
    case 'NUMBER':
    case 'INT':
      return [
        {
          id,
          title,
          input: {
            kind: 'suffix' as const,
            suffix: '',
            required,
            inputType: 'number' as const,
            placeholder: '0',
          },
        },
      ];
    case 'DATE':
      return [
        {
          id,
          title,
          input: { kind: 'date' as const, required },
        },
      ];
    case 'TIME':
      return [
        {
          id,
          title,
          // 모바일 친화적 wheel picker — 이사 시간 그라뉼래리티는 1시간 단위가 기본.
          // 더 세밀한 단위가 필요하면 spec 에서 별도로 명시(현재 spec 에는 step 정보 없음).
          input: {
            kind: 'time' as const,
            required,
            minuteStep: 60,
          },
        },
      ];
    case 'STRUCTURED_DEPARTURE_ADDRESS':
      return [
        {
          id,
          title,
          // 도착지 주소 흐름과 동일한 BottomSheet 도로명 검색.
          input: {
            kind: 'address' as const,
            required,
            placeholder: '도로명 주소 검색',
          },
        },
      ];
    case 'DATE_RANGE':
      // 시작·종료 2개 question 으로 분해. id 는 attr.key__start / __end.
      return [
        {
          id: `${id}${DATE_RANGE_START_SUFFIX}`,
          title: `${title} 시작일`,
          input: { kind: 'date' as const, required },
        },
        {
          id: `${id}${DATE_RANGE_END_SUFFIX}`,
          title: `${title} 종료일`,
          input: { kind: 'date' as const, required },
        },
      ];
    case 'CHOICE_WITH_CUSTOM': {
      const enumOpts = (resolved.enumValues ?? []).map((v) => ({
        value: v,
        label: humanizeEnumValue(v, resolved.enumDisplayNames),
      }));
      // 사용자 직접 입력 트리거 옵션을 자동 추가 — spec 의 enumValues 에 이미 있으면
      // 중복 추가하지 않는다. label 은 "직접 입력".
      const hasCustomTrigger = enumOpts.some((o) => o.value === CHOICE_CUSTOM_TRIGGER);
      const opts = hasCustomTrigger
        ? enumOpts
        : [...enumOpts, { value: CHOICE_CUSTOM_TRIGGER, label: '직접 입력' }];
      return [
        {
          id,
          title,
          input: {
            kind: 'single-select' as const,
            required,
            options: opts,
            extensible: {
              triggerValue: CHOICE_CUSTOM_TRIGGER,
              sheetTitle: `${title} 직접 입력`,
              placeholder: '직접 입력',
              confirmLabel: '확인',
            },
          },
        },
      ];
    }
    case 'ENUM': {
      const opts = (resolved.enumValues ?? []).map((v) => ({
        value: v,
        label: humanizeEnumValue(v, resolved.enumDisplayNames),
      }));
      // enum 값 정의가 없으면 안전하게 text 로 fallback (잘못된 spec 대응)
      if (opts.length === 0) {
        return [
          {
            id,
            title,
            input: { kind: 'text' as const, required },
          },
        ];
      }
      return [
        {
          id,
          title,
          input: { kind: 'single-select' as const, required, options: opts },
        },
      ];
    }
    case 'STRING':
    default:
      return [
        {
          id,
          title,
          input: { kind: 'text' as const, required },
        },
      ];
  }
}

/**
 * MovingTypeSpecificationResponse 를 인스턴스 작성용 sub-FlowDocument 로 변환한다.
 *
 * 결과는 ItemPicker item.flow 에 inline 으로 주입되어 카드 탭 시 SubFlowOverlay 에서
 * 렌더된다. attribute 가 없는 spec(예: 단순 분류만 하는 유형)은 빈 questions 배열을
 * 갖는 step → 즉시 terminal 로 도달해 attributes 없이 인스턴스가 추가된다.
 */
export function movingTypeSpecToFlowDocument(
  spec: MovingTypeSpecificationResponse,
  options?: MovingTypeSpecToFlowOptions
): FlowDocument {
  // attribute 한 개가 1+ question 으로 확장될 수 있다(DATE_RANGE 는 2 sub-question).
  const baseQuestions: QuestionDoc[] = resolveMovingTypeFlowAttributes(spec).flatMap((attr) =>
    attrToQuestions(attr, resolveMovingTypeAttribute(attr), options)
  );

  const questions: QuestionDoc[] = baseQuestions.map((q, idx) => {
    const isLast = idx === baseQuestions.length - 1;
    return {
      ...q,
      layout: SUB_FLOW_LAYOUT,
      ctaLabel: isLast ? COMPLETE_LABEL : NEXT_LABEL,
    };
  });

  // spec.helpText 도 backend 디버그성 영문일 가능성이 있어 infoMessage 매핑 제외.
  const meta: FlowDocument['meta'] = {
    appBarTitle: spec.name,
  };

  return {
    schemaVersion: 1,
    id: `moving-type-spec-flow-${spec.code}`,
    initial: SUB_FLOW_STEP_ID,
    meta,
    steps: {
      [SUB_FLOW_STEP_ID]: {
        kind: 'questions' as const,
        questions,
        next: { kind: 'terminal' as const, target: TERMINAL_DONE },
      },
    },
    terminals: {
      [TERMINAL_DONE]: { kind: 'placeholder-auto' },
    },
  };
}
